import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage } from '../../api/globalSettings';
import { getFlagIcon } from '../common/languageFlags';
import '../../styles/lang-select.css';

// The language's name in the interface language ("Mongolian" in an English
// UI), or '' where the browser can't name it.
function localizedName(code: string, uiLanguage: string): string {
    try {
        return new Intl.DisplayNames([uiLanguage, 'en'], { type: 'language' }).of(code) ?? '';
    } catch {
        return '';
    }
}

// Each language under its own name, plus the interface language's name when
// that differs: a list of endonyms alone ("Монгол", "فارسی") hid languages
// from users looking for "Mongolian" or "Persian", who picked "not listed"
// instead (2026-10). Sorted by the name the user reads.
export function languageLabels(languages: SupportedLanguage[], uiLanguage: string) {
    return languages
        .map(lang => {
            const own = lang.native_name || lang.name;
            const local = localizedName(lang.code, uiLanguage) || lang.name;
            const secondary = local.toLocaleLowerCase() === own.toLocaleLowerCase() ? '' : local;
            return { lang, own, secondary, sortKey: local };
        })
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey, uiLanguage));
}

interface Props {
    languages: SupportedLanguage[];
    value: string;
    onChange: (code: string) => void;
    placeholder: string;
}

const LanguageSelect: React.FC<Props> = ({ languages, value, onChange, placeholder }) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const labels = useMemo(() => languageLabels(languages, i18n.language || 'en'), [languages, i18n.language]);
    const selected = labels.find(l => l.lang.code === value) ?? null;

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const handleSelect = (code: string) => {
        onChange(code);
        setOpen(false);
    };

    const name = (l: { own: string; secondary: string }) => (
        <span className="lang-select-name">
            {l.own}
            {l.secondary && <span className="lang-select-secondary"> · {l.secondary}</span>}
        </span>
    );

    return (
        <div className="lang-select" ref={ref}>
            <button
                type="button"
                className={`lang-select-trigger form-input${open ? ' open' : ''}`}
                onClick={() => setOpen(v => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selected ? (
                    <>
                        <span className="lang-select-flag">{getFlagIcon(selected.lang.code)}</span>
                        {name(selected)}
                    </>
                ) : (
                    <span className="lang-select-placeholder">{placeholder}</span>
                )}
                <span className="lang-select-chevron" aria-hidden>▾</span>
            </button>

            {open && (
                <ul className="lang-select-dropdown" role="listbox">
                    {labels.map(l => (
                        <li
                            key={l.lang.code}
                            role="option"
                            aria-selected={l.lang.code === value}
                            className={`lang-select-option${l.lang.code === value ? ' selected' : ''}`}
                            onMouseDown={() => handleSelect(l.lang.code)}
                        >
                            <span className="lang-select-flag">{getFlagIcon(l.lang.code)}</span>
                            {name(l)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default LanguageSelect;
